import { useEffect, useRef, useState } from "react";
import { FilesetResolver, PoseLandmarker } from "@mediapipe/tasks-vision";
import { api } from "../lib/api";
import { useMe } from "../hooks/useQueries";
import { PageSkeleton } from "../components/PageSkeleton";
import { OmniCodeChatPanel } from "../components/OmniCodeChatPanel";

// Private admin source packages have a higher allowance than customer renders.
// The backend and reverse proxy leave room for the base64 request envelope.
const MAX_PRIVATE_DRIVER_BYTES = 120 * 1024 * 1024;
const POSE_WASM_ROOT = "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@1.0.1/wasm";
const POSE_MODEL_URL = "https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/latest/pose_landmarker_lite.task";
const BODY_CAPTURE_CLIPS = [
  { id: "frame", bodyPart: "Full frame", label: "1. Framing check", seconds: 4, instruction: "Stand still, facing the camera. Keep your full head, both hands, legs, shoes, and a little floor visible." },
  { id: "head", bodyPart: "Head & face", label: "2. Head movement", seconds: 5, instruction: "Keep your body still. Look center, then make one slow left and right head turn. Return to center." },
  { id: "torso", bodyPart: "Shoulders & torso", label: "3. Torso movement", seconds: 5, instruction: "Relax your shoulders, shift your weight once, and make one small natural torso turn." },
  { id: "hands", bodyPart: "Arms & hands", label: "4. Open-hand gesture", seconds: 6, instruction: "Keep elbows away from your torso. Make two small open-palm gestures below chest height; do not clasp or cross hands." },
  { id: "legs", bodyPart: "Legs & feet", label: "5. Lower-body movement", seconds: 6, instruction: "Shift weight naturally, take one side step, then return. Keep shoes and floor margin in frame." },
  { id: "walk", bodyPart: "Whole body", label: "6. Natural walk", seconds: 8, instruction: "Walk four relaxed steps toward the camera. Keep both hands open, below the waist, and away from your torso." },
  { id: "turn", bodyPart: "Whole body", label: "7. Body turn", seconds: 6, instruction: "Make one slow quarter-turn and return to center. Keep your whole body and both hands in frame." },
];

function isSupportedPrivateVideo(file) {
  if (!file) return false;
  return ["video/mp4", "video/webm", "video/quicktime"].includes(file.type)
    || /\.(mp4|webm|mov)$/i.test(file.name || "");
}
const PRODUCT_WORK_QUEUE = [
  ["Timed publishing", "Customers can save hourly, daily, or weekly content recipes. Generation and posting stay blocked until the connected provider and policy gate approve a run.", "Scheduling UI next"],
  ["Private learning monitor", "Consent-only QA metadata is visible below; private source media and customer identities remain inaccessible.", "Live"],
  ["Customer notices", "Compose maintenance or product-update banners with start and expiry dates below.", "Live"],
  ["OmniCode Builder", "Customers can turn an idea or existing project into an AI build blueprint with transparent credits and approval-gated next steps.", "Live"],
  ["Marketplace expansion", "Existing template packs remain installable. New inventory is not advertised until it is actually created and reviewed.", "In scope"],
];

const OMNICODE_EXECUTION_QUEUE = [
  ["Customer-action audit", "Make every customer button work, clearly explain its limitation, or hide it. Keep an evidence log for each result.", "audit"],
  ["Connections cockpit", "Finish provider-specific status details, redirects, setup guidance, and secure customer-owned OAuth flows. Never expose credentials.", "connections"],
  ["Publishing safety", "Keep Meta and YouTube publishing approval-gated; TikTok posting stays blocked until review approval. Never auto-post.", "policy"],
  ["Scheduled content", "Use durable run records, rights confirmation, credit holds/releases, policy review, and an explicit post approval before every scheduled publish.", "scheduler"],
  ["Avatar quality", "Keep customer full-body disabled until head-to-toe framing, motion, lip-sync, stability, and human QA gates pass.", "avatar"],
  ["OmniCode product", "Improve the Builder and Admin cockpit with transparent stages, customer-owned connections, reviewable changes, rollback history, tools, and saved chats.", "omnicode"],
  ["App Directory", "Keep the public and in-product directory device-friendly, truthful, and limited to reviewed real listings. Provide browser access for iPhone users.", "directory"],
  ["Platform readiness", "Complete Shopify review and controlled connection tests. Keep optional platforms out of marketing until each secure OAuth integration is approved.", "platforms"],
  ["Customer operations", "Maintain credit estimates, managed-hosting clarity, notices, usage caps, monitoring, backups, and accurate wait-time/cost communication.", "operations"],
];

const FULL_OMNICODE_EXECUTION_BRIEF = `OmniMark execution queue — work through these items in order. For every item: inspect the current implementation, make only the smallest safe scoped change, validate it, record visible progress, and stop for human review when a policy, external provider, credential, billing, publishing, app-store, deployment, or destructive action is involved.\n\n${OMNICODE_EXECUTION_QUEUE.map(([title, detail], index) => `${index + 1}. ${title}: ${detail}`).join("\n\n")}\n\nNever claim a connection, build, upload, listing, charge, or deployment is complete unless evidence is recorded. Preserve customer ownership and never put secrets in prompts or source code.`;

export default function AdminPage() {
  const { data: user, isLoading: userLoading } = useMe();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [supportRequests, setSupportRequests] = useState([]);
  const [creatorEarnings, setCreatorEarnings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [actionBusy, setActionBusy] = useState(null);
  const [qualityMonitor, setQualityMonitor] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [noticeTitle, setNoticeTitle] = useState("");
  const [noticeBody, setNoticeBody] = useState("");
  const [noticeStartsAt, setNoticeStartsAt] = useState("");
  const [noticeEndsAt, setNoticeEndsAt] = useState("");
  const [omniCodeRequest, setOmniCodeRequest] = useState("");
  const [omniCodePlan, setOmniCodePlan] = useState(null);
  const [omniCodeBusy, setOmniCodeBusy] = useState(false);
  const [omniCodeExecution, setOmniCodeExecution] = useState(null);
  const [omniCodeRemoteUrl, setOmniCodeRemoteUrl] = useState("");
  const [omniCodeToolStatus, setOmniCodeToolStatus] = useState("");

  function appendOmniCodeContext(text) {
    setOmniCodeRequest((current) => `${current}${current.trim() ? "\n\n" : ""}${text}`);
    setOmniCodePlan(null);
    setOmniCodeExecution(null);
  }

  function openOmniChatAdmin() {
    document.getElementById("omnichat-admin")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  async function addOmniCodeClipboard() {
    setOmniCodeToolStatus("");
    if (!window.confirm("Allow OmniCode Admin to add the current clipboard to this code brief? Do not approve passwords, API keys, tokens, or private keys—save those in Build Vault instead.")) return setOmniCodeToolStatus("Clipboard access was not granted.");
    try {
      const text = await navigator.clipboard.readText();
      if (!text.trim()) return setOmniCodeToolStatus("Your clipboard is empty.");
      if (/(?:-----BEGIN|sk-[A-Za-z0-9]|ghp_[A-Za-z0-9]|github_pat_|AKIA[0-9A-Z]{16}|xox[baprs]-|eyJ[a-zA-Z0-9_-]{10,}\.)/.test(text)) return setOmniCodeToolStatus("That looks like a secret. It was not added; save it in Build Vault instead.");
      appendOmniCodeContext(`Clipboard context (shared after approval):\n${text}`);
      setOmniCodeToolStatus("Clipboard content was added to the brief. Review it before running OmniCode.");
    } catch (_) { setOmniCodeToolStatus("Clipboard access was unavailable or denied by the browser."); }
  }

  function addOmniCodeRemoteUrl() {
    let url;
    try { url = new URL(omniCodeRemoteUrl.trim()); } catch (_) { return setOmniCodeToolStatus("Enter a valid HTTPS URL."); }
    if (url.protocol !== "https:") return setOmniCodeToolStatus("Only HTTPS URLs can be attached.");
    appendOmniCodeContext(`Remote source to inspect before coding: ${url.toString()}`);
    setOmniCodeRemoteUrl("");
    setOmniCodeToolStatus("Remote source was added to the brief. OmniCode will not access it until the run is started.");
  }

  useEffect(() => {
    if (!user?.is_admin) return;
    loadData();
  }, [user?.is_admin]);

  async function loadData() {
    setLoading(true);
    setErr(null);
    try {
      const [statsRes, usersRes, wsRes, supportRes, earningsRes, monitorRes, noticesRes] = await Promise.all([
        api.get("/api/admin/stats"),
        api.get("/api/admin/users"),
        api.get("/api/admin/workspaces"),
        api.get("/api/admin/support-requests"),
        api.get("/api/admin/creator-earnings").catch(() => ({ data: { items: [] } })),
        api.get("/api/admin/private-quality-monitor").catch(() => ({ data: null })),
        api.get("/api/admin/announcements").catch(() => ({ data: { items: [] } })),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.items || []);
      setWorkspaces(wsRes.data.items || []);
      setSupportRequests(supportRes.data.items || []);
      setCreatorEarnings(earningsRes.data.items || []);
      setQualityMonitor(monitorRes.data);
      setAnnouncements(noticesRes.data.items || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load admin data");
    } finally {
      setLoading(false);
    }
  }

  async function setPlan(userId, plan) {
    setActionBusy(userId);
    try {
      await api.post(`/api/admin/users/${userId}/set-plan`, { plan });
      await loadData();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to set plan");
    } finally {
      setActionBusy(null);
    }
  }

  async function addCredits(userId) {
    const amount = prompt("Enter credit amount to add:");
    if (!amount || isNaN(amount)) return;
    
    setActionBusy(userId);
    try {
      await api.post(`/api/admin/users/${userId}/add-credits`, { amount: parseInt(amount) });
      alert(`Added ${amount} credits`);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to add credits");
    } finally {
      setActionBusy(null);
    }
  }

  async function reviewCreatorEarning(earningId, decision) {
    const approved = decision === "approve";
    if (!window.confirm(`Confirm ${approved ? "approval" : "rejection"} of this earnings proposal. This records an Admin decision but does not send money.`)) return;
    setActionBusy(earningId);
    try {
      const reviewNote = prompt("Optional review note:") || (approved ? "Approved by OmniMark Admin." : "Rejected by OmniMark Admin.");
      await api.post(`/api/admin/creator-earnings/${earningId}/${decision}`, { review_note: reviewNote });
      await loadData();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not review creator earnings.");
    } finally {
      setActionBusy(null);
    }
  }

  async function createNotice() {
    setActionBusy("notice");
    try {
      await api.post("/api/admin/announcements", {
        title: noticeTitle.trim(), body: noticeBody.trim(), type: "banner",
        starts_at: noticeStartsAt ? new Date(noticeStartsAt).toISOString() : null,
        ends_at: noticeEndsAt ? new Date(noticeEndsAt).toISOString() : null,
      });
      setNoticeTitle(""); setNoticeBody(""); setNoticeStartsAt(""); setNoticeEndsAt("");
      await loadData();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not create the customer notice.");
    } finally { setActionBusy(null); }
  }

  async function createOmniCodePlan() {
    const request = omniCodeRequest.trim();
    if (!request) return;
    setOmniCodeBusy(true);
    setErr(null);
    try {
      const response = await api.post("/api/admin/omnicode/assist", { request });
      setOmniCodePlan(response.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "OmniCode could not create a plan right now.");
    } finally {
      setOmniCodeBusy(false);
    }
  }

  async function runOmniCodeExecution() {
    const request = omniCodeRequest.trim();
    if (!request) return;
    setOmniCodeBusy(true);
    setErr(null);
    try {
      const response = await api.post("/api/admin/omnicode/executions", { request });
      setOmniCodeExecution(response.data);
      const checkRun = async () => {
        try {
          const latest = await api.get(`/api/admin/omnicode/executions/${response.data.id}`);
          setOmniCodeExecution(latest.data);
          if (["queued", "planning", "editing", "testing"].includes(latest.data.status)) window.setTimeout(checkRun, 2500);
        } catch (_) { /* The initial queue record remains visible. */ }
      };
      window.setTimeout(checkRun, 1200);
    } catch (e) {
      setErr(e?.response?.data?.detail || "OmniCode could not start an isolated run.");
    } finally {
      setOmniCodeBusy(false);
    }
  }

  async function commitOmniCodeExecution() {
    if (!omniCodeExecution?.id || !window.confirm("Save this validated low-risk change to an isolated Git branch? This will not merge, deploy, publish, charge, or alter production.")) return;
    setOmniCodeBusy(true);
    setErr(null);
    try {
      const response = await api.post(`/api/admin/omnicode/executions/${omniCodeExecution.id}/commit`);
      setOmniCodeExecution((current) => ({ ...current, ...response.data }));
    } catch (e) {
      setErr(e?.response?.data?.detail || "OmniCode could not save the validated branch.");
    } finally {
      setOmniCodeBusy(false);
    }
  }

  function loadExecutionItem(title, detail) {
    setOmniCodeRequest(`Task: ${title}\n\n${detail}\n\nInspect the current OmniMark implementation first. Make the smallest safe change, validate it, and report exactly what changed. Do not alter credentials, provider scopes, billing, publishing, migrations, deployment settings, or customer data unless a separate scoped approval is recorded.`);
    setOmniCodePlan(null); setOmniCodeExecution(null);
  }

  if (userLoading) return <PageSkeleton />;

  if (!user?.is_admin) {
    return (
      <div style={{ padding: 24, textAlign: "center" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
        <div style={{ fontSize: 18, fontWeight: 700 }}>Admin Access Required</div>
        <div style={{ color: "#666", marginTop: 8 }}>
          You don't have permission to view this page.
        </div>
      </div>
    );
  }

  if (loading) return <PageSkeleton />;

  return (
    <div style={{ padding: 16, maxWidth: 1200, margin: "0 auto" }} data-testid="admin-page">
      <div style={{ fontSize: 24, fontWeight: 900 }}>Admin Dashboard</div>
      <div style={{ color: "#666", marginTop: 6 }}>
        Platform analytics and user management
      </div>

      <section style={{ ...card, marginTop: 16, borderColor: "#c7d2fe", background: "linear-gradient(135deg, #f8faff, #f5f3ff)" }}>
        <div style={{ fontWeight: 900, fontSize: 17 }}>Product work queue</div>
        <div style={{ marginTop: 4, color: "#4b5563", fontSize: 13 }}>Authorized priorities. These remain private Admin planning items until their implementation and quality gates are complete.</div>
        <div style={{ display: "grid", gap: 8, marginTop: 12 }}>
          {PRODUCT_WORK_QUEUE.map(([title, detail, status]) => (
            <div key={title} style={{ padding: 10, borderRadius: 9, background: "#fff", border: "1px solid #e5e7eb" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "baseline" }}><b>{title}</b><span style={{ color: "#5b21b6", fontSize: 12, fontWeight: 700 }}>{status}</span></div>
              <div style={{ marginTop: 4, color: "#4b5563", fontSize: 13 }}>{detail}</div>
            </div>
          ))}
        </div>
      </section>

      <section style={{ ...card, marginTop: 16, borderColor: "#ddd6fe", background: "linear-gradient(135deg, #fbfaff, #f5f3ff)" }} data-testid="omnicode-execution-queue">
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "baseline", flexWrap: "wrap" }}><div><div style={{ fontWeight: 900, fontSize: 17 }}>OmniCode execution queue</div><div style={{ marginTop: 4, color: "#4b5563", fontSize: 13 }}>Load the full queue or write a detailed brief. OmniCode plans the whole request and prepares every applicable low-risk change in one isolated review; protected work is clearly flagged instead of silently skipped.</div></div><button onClick={() => { setOmniCodeRequest(FULL_OMNICODE_EXECUTION_BRIEF); setOmniCodePlan(null); setOmniCodeExecution(null); }} style={{ ...btnSmall, background: "#5b21b6", color: "#fff" }}>Load task queue</button></div>
        <div style={{ display: "grid", gap: 8, marginTop: 12 }}>{OMNICODE_EXECUTION_QUEUE.map(([title, detail, status]) => <div key={title} style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", gap: 10, alignItems: "center", padding: 10, borderRadius: 9, background: "#fff", border: "1px solid #e9d5ff" }}><div><div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}><b>{title}</b><span style={{ color: "#6d28d9", fontSize: 11, fontWeight: 800 }}>{status}</span></div><div style={{ marginTop: 3, color: "#4b5563", fontSize: 12 }}>{detail}</div></div><button onClick={() => loadExecutionItem(title, detail)} style={btnSmall}>Load task</button></div>)}</div>
      </section>

      <section style={{ ...card, marginTop: 16, borderColor: "#a5b4fc", background: "linear-gradient(135deg, #eff6ff, #faf5ff)" }} data-testid="omnicode-admin-assistant">
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "baseline", flexWrap: "wrap" }}>
          <div>
            <div style={{ fontWeight: 900, fontSize: 18 }}>OmniCode Admin</div>
            <div style={{ marginTop: 4, color: "#4b5563", fontSize: 13 }}>Describe the product change in your own words. OmniCode turns it into visible coding jobs, prepares and tests the applicable safe changes, and never merges or deploys automatically.</div>
          </div>
          <span style={{ color: "#4338ca", fontSize: 12, fontWeight: 800 }}>PLAN MODE</span>
        </div>
        <div style={{ marginTop: 12, padding: 10, borderRadius: 9, background: "#fff", border: "1px solid #ddd6fe" }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#312e81" }}>OmniCode tools & context</div>
          <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginTop: 8 }}>
            <button onClick={openOmniChatAdmin} style={btnSmall}>Chat + library</button>
            <button onClick={() => appendOmniCodeContext("Research current sources before proposing the code change; cite the URLs in the review summary.")} style={btnSmall}>Research</button>
            <button onClick={() => appendOmniCodeContext("Review this request for security, privacy, policy, rollback, and customer-impact risks before editing code.")} style={btnSmall}>Review</button>
            <button onClick={() => appendOmniCodeContext("For any provider connection, use customer-owned secure OAuth and Build Vault references only. Never put credentials in source or prompts.")} style={btnSmall}>Connections</button>
            <button onClick={addOmniCodeClipboard} style={btnSmall}>Clipboard</button>
            <button onClick={() => appendOmniCodeContext("Use available Build Vault secret references by environment-variable name only. Never reveal, log, commit, or request the secret value.")} style={btnSmall}>Vault</button>
          </div>
          <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginTop: 8 }}>
            <input value={omniCodeRemoteUrl} onChange={(e) => setOmniCodeRemoteUrl(e.target.value)} placeholder="https://remote-source.example" style={{ ...selectStyle, flex: "1 1 260px" }} />
            <button onClick={addOmniCodeRemoteUrl} style={btnSmall}>Attach remote URL</button>
          </div>
          {omniCodeToolStatus && <div style={{ marginTop: 7, color: "#4338ca", fontSize: 12 }}>{omniCodeToolStatus}</div>}
        </div>
        <textarea
          value={omniCodeRequest}
          onChange={(e) => setOmniCodeRequest(e.target.value)}
          maxLength={20000}
          rows={9}
          placeholder="Describe the full outcome, requirements, problems, examples, and constraints. OmniCode will organize the work into coding jobs."
          style={{ ...selectStyle, width: "100%", boxSizing: "border-box", marginTop: 12, resize: "vertical" }}
        />
        <div style={{ display: "flex", gap: 10, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", marginTop: 10 }}>
          <div style={{ color: "#4b5563", fontSize: 12 }}>Run isolated change writes and tests code in a disposable workspace. A validated low-risk result can then be saved to its own Git branch; it never merges or deploys itself.</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}><button onClick={createOmniCodePlan} disabled={omniCodeBusy || !omniCodeRequest.trim()} style={btnSmall}>Create plan</button><button onClick={runOmniCodeExecution} disabled={omniCodeBusy || !omniCodeRequest.trim()} style={{ ...btnSmall, background: "#312e81", color: "#fff" }}>{omniCodeBusy ? "Starting…" : "Run isolated change"}</button></div>
        </div>
        {omniCodePlan && <div style={{ display: "grid", gap: 10, marginTop: 14 }}>
          <div style={{ padding: 11, borderRadius: 9, background: "#fff", border: "1px solid #dbeafe" }}><b>Recommendation</b><div style={{ marginTop: 4, color: "#374151", whiteSpace: "pre-wrap" }}>{omniCodePlan.summary}</div></div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 }}>
            <PlanList title="Likely areas" items={omniCodePlan.recommended_files} empty="No file assumptions provided." />
            <PlanList title="Verification" items={omniCodePlan.verification} empty="Verification steps will be scoped during implementation." />
          </div>
          <PlanList title="Proposed steps" items={omniCodePlan.steps} empty="No implementation steps returned." />
          <PlanList title="Risks and approvals" items={omniCodePlan.risks} empty="No material risks identified; confirm scope before implementation." tone="#fff7ed" />
          <div style={{ color: "#4338ca", fontSize: 12, fontWeight: 700 }}>{omniCodePlan.needs_approval || "A separate, scoped instruction is required before any implementation."}</div>
        </div>}
        {omniCodeExecution && <div style={{ marginTop: 14, padding: 11, borderRadius: 9, background: "#fff", border: "1px solid #ddd6fe" }}><div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}><b>Isolated code run</b><span style={{ color: "#4338ca", fontWeight: 800, fontSize: 12 }}>{String(omniCodeExecution.status || "queued").replaceAll("_", " ")}</span></div><div style={{ marginTop: 5, color: "#374151", fontSize: 13 }}>{omniCodeExecution.summary || omniCodeExecution.message || "Preparing a bounded workspace and validation run."}</div>{(omniCodeExecution.changed_files || []).length > 0 && <div style={{ marginTop: 7, fontSize: 12 }}>Changed: {omniCodeExecution.changed_files.join(", ")}</div>}{omniCodeExecution.status === "ready_for_review" && <button onClick={commitOmniCodeExecution} disabled={omniCodeBusy || omniCodeExecution.risk_tier !== "low"} style={{ ...btnSmall, marginTop: 10, background: "#166534", color: "#fff" }}>{omniCodeBusy ? "Saving…" : "Save validated code to Git branch"}</button>}{omniCodeExecution.status === "ready_for_review" && omniCodeExecution.risk_tier !== "low" && <div style={{ marginTop: 8, color: "#92400e", fontSize: 12 }}>This run needs review because it is not classified low risk.</div>}{omniCodeExecution.branch_name && <div style={{ marginTop: 8, color: "#166534", fontSize: 12 }}>Saved branch: {omniCodeExecution.branch_name}. It has not been merged or deployed.</div>}{omniCodeExecution.test_log && <pre style={{ marginTop: 8, maxHeight: 160, overflow: "auto", padding: 8, borderRadius: 6, background: "#111827", color: "#e5e7eb", fontSize: 11, whiteSpace: "pre-wrap" }}>{omniCodeExecution.test_log}</pre>}{omniCodeExecution.error_detail && <div style={{ marginTop: 8, color: "#b00020", fontSize: 12 }}>{omniCodeExecution.error_detail}</div>}</div>}
      </section>

      <div id="omnichat-admin" style={{ marginTop: 16 }}><OmniCodeChatPanel context="admin" /></div>

      {err && <div style={{ marginTop: 10, color: "#b00020" }}>{err}</div>}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 14, marginTop: 16 }}>
        <section style={card}>
          <div style={{ fontSize: 17, fontWeight: 900 }}>Customer notice</div>
          <div style={{ marginTop: 4, color: "#666", fontSize: 13 }}>Shows as a dismissible in-app banner. Publish only accurate maintenance or product information.</div>
          <input value={noticeTitle} onChange={(e) => setNoticeTitle(e.target.value)} maxLength={140} placeholder="Title, e.g. Scheduled maintenance" style={{ ...selectStyle, width: "100%", boxSizing: "border-box", marginTop: 10 }} />
          <textarea value={noticeBody} onChange={(e) => setNoticeBody(e.target.value)} maxLength={1000} placeholder="What customers need to know" rows={3} style={{ ...selectStyle, width: "100%", boxSizing: "border-box", marginTop: 8, resize: "vertical" }} />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8 }}>
            <label style={{ fontSize: 12 }}>Start<input type="datetime-local" value={noticeStartsAt} onChange={(e) => setNoticeStartsAt(e.target.value)} style={{ ...selectStyle, display: "block", width: "100%", boxSizing: "border-box", marginTop: 3 }} /></label>
            <label style={{ fontSize: 12 }}>Expires<input type="datetime-local" value={noticeEndsAt} onChange={(e) => setNoticeEndsAt(e.target.value)} style={{ ...selectStyle, display: "block", width: "100%", boxSizing: "border-box", marginTop: 3 }} /></label>
          </div>
          <button onClick={createNotice} disabled={actionBusy === "notice" || !noticeTitle.trim() || !noticeBody.trim()} style={{ ...btnSmall, marginTop: 10, background: "#111827", color: "#fff" }}>{actionBusy === "notice" ? "Saving…" : "Publish customer notice"}</button>
          {announcements.length > 0 && <div style={{ marginTop: 10, fontSize: 12, color: "#666" }}>Recent: {announcements.slice(0, 2).map((item) => item.title).join(" · ")}</div>}
        </section>

        <section style={card}>
          <div style={{ fontSize: 17, fontWeight: 900 }}>Private quality monitor</div>
          <div style={{ marginTop: 4, color: "#666", fontSize: 13 }}>{qualityMonitor?.message || "Loading consented quality metadata…"}</div>
          {qualityMonitor?.available && <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 10 }}>
              <StatCard label="Private packages" value={Object.values(qualityMonitor.summary?.submissions || {}).reduce((sum, count) => sum + count, 0)} />
              <StatCard label="QA renders" value={Object.values(qualityMonitor.summary?.qa_jobs || {}).reduce((sum, count) => sum + count, 0)} />
            </div>
            <div style={{ marginTop: 10, display: "grid", gap: 6 }}>{(qualityMonitor.recent_jobs || []).slice(0, 3).map((job) => <div key={job.id} style={{ borderTop: "1px solid #eee", paddingTop: 6, fontSize: 12 }}><b>{job.mode}</b> · {job.duration_seconds || "?"}s · {job.status} · visual review: {job.visual_review_status}</div>)}</div>
          </>}
        </section>
      </div>

      {/* Stats */}
      <div style={{ marginTop: 20, display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
        <StatCard label="Users" value={stats?.users_count || 0} />
        <StatCard label="Workspaces" value={stats?.workspaces_count || 0} />
        <StatCard label="Generations" value={stats?.generations_count || 0} />
        <StatCard label="Packs" value={stats?.packs_count || 0} />
      </div>

      {/* Users */}
      <div style={{ marginTop: 24 }}>
        <div style={{ fontSize: 18, fontWeight: 900 }}>Users</div>
        <div style={{ marginTop: 12, display: "grid", gap: 8 }}>
          {users.map((u) => (
            <div key={u.id} style={card}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontWeight: 700 }}>{u.email}</div>
                  <div style={{ fontSize: 12, color: "#666" }}>
                    Plan: <span style={{ fontWeight: 600 }}>{u.plan || "trial"}</span> • 
                    Joined: {new Date(u.created_at).toLocaleDateString()}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <select
                    value={u.plan || "trial"}
                    onChange={(e) => setPlan(u.id, e.target.value)}
                    disabled={actionBusy === u.id}
                    style={selectStyle}
                  >
                    <option value="trial">Trial</option>
                    <option value="starter">Starter</option>
                    <option value="pro">Pro</option>
                    <option value="agency">Agency</option>
                  </select>
                  <button
                    onClick={() => addCredits(u.id)}
                    disabled={actionBusy === u.id}
                    style={btnSmall}
                  >
                    + Credits
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Workspaces */}
      <div style={{ marginTop: 24 }}>
        <div style={{ fontSize: 18, fontWeight: 900 }}>Workspaces</div>
        <div style={{ marginTop: 12, display: "grid", gap: 8 }}>
          {workspaces.map((w) => (
            <div key={w.id} style={card}>
              <div style={{ fontWeight: 700 }}>{w.name}</div>
              <div style={{ fontSize: 12, color: "#666" }}>
                Created: {new Date(w.created_at).toLocaleDateString()}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ marginTop: 24 }}>
        <div style={{ fontSize: 18, fontWeight: 900 }}>Support inbox</div>
        <div style={{ marginTop: 12, display: "grid", gap: 8 }}>
          {supportRequests.length === 0 && <div style={{ color: "#666" }}>No support requests yet.</div>}
          {supportRequests.map((request) => (
            <div key={request.id} style={card}>
              <div style={{ fontWeight: 700 }}>{request.subject}</div>
              <div style={{ fontSize: 13, color: "#666", marginTop: 4 }}>{request.name} · <a href={`mailto:${request.email}`}>{request.email}</a> · {new Date(request.created_at).toLocaleString()}</div>
              <div style={{ marginTop: 8, whiteSpace: "pre-wrap" }}>{request.message}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ marginTop: 24 }} data-testid="creator-earnings-admin">
        <div style={{ fontSize: 18, fontWeight: 900 }}>Creator earnings approvals</div>
        <div style={{ marginTop: 4, color: "#666", fontSize: 13 }}>10% OmniMark platform-charge proposals. Customer funds settle to OmniMark first; views are never treated as money, and approval prepares a creator payout without sending one automatically.</div>
        <div style={{ marginTop: 12, display: "grid", gap: 8 }}>
          {creatorEarnings.length === 0 && <div style={{ color: "#666" }}>No creator earnings proposals yet.</div>}
          {creatorEarnings.map((earning) => (
            <div key={earning.id} style={card}>
              <div style={{ display: "flex", gap: 10, justifyContent: "space-between", flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontWeight: 700 }}>${(Number(earning.gross_amount_cents || 0) / 100).toFixed(2)} · {String(earning.source_type || "").replaceAll("_", " ")}</div>
                  <div style={{ fontSize: 12, color: "#666", marginTop: 3 }}>Creator ${(Number(earning.creator_net_cents || 0) / 100).toFixed(2)} · Platform fee ${(Number(earning.platform_fee_cents || 0) / 100).toFixed(2)} · {earning.source_reference}</div>
                  {earning.review_note && <div style={{ fontSize: 12, color: "#666", marginTop: 3 }}>Admin note: {earning.review_note}</div>}
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <span style={{ fontSize: 12, fontWeight: 700 }}>{String(earning.status || "").replaceAll("_", " ")}</span>
                  {earning.status === "pending_admin_review" && <><button onClick={() => reviewCreatorEarning(earning.id, "approve")} disabled={actionBusy === earning.id} style={{ ...btnSmall, background: "#047857", color: "#fff" }}>Approve</button><button onClick={() => reviewCreatorEarning(earning.id, "reject")} disabled={actionBusy === earning.id} style={btnSmall}>Reject</button></>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <AvatarPerformanceLab />
    </div>
  );
}

function PlanList({ title, items, empty, tone = "#fff" }) {
  const values = Array.isArray(items) ? items.filter(Boolean) : [];
  return (
    <div style={{ padding: 11, borderRadius: 9, background: tone, border: "1px solid #e5e7eb" }}>
      <div style={{ fontWeight: 800 }}>{title}</div>
      {values.length ? <ul style={{ margin: "7px 0 0", paddingLeft: 18, color: "#374151", fontSize: 13, display: "grid", gap: 4 }}>
        {values.map((item, index) => <li key={`${title}-${index}`}>{String(item)}</li>)}
      </ul> : <div style={{ marginTop: 6, color: "#6b7280", fontSize: 13 }}>{empty}</div>}
    </div>
  );
}

function dataUrlFor(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Could not read the selected file."));
    reader.readAsDataURL(blob);
  });
}

function AvatarPerformanceLab() {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const timeoutRef = useRef(null);
  const motionIntervalRef = useRef(null);
  const poseIntervalRef = useRef(null);
  const poseLandmarkerRef = useRef(null);
  const previousMotionFrameRef = useRef(null);
  const [cameraState, setCameraState] = useState("idle");
  const [identityImage, setIdentityImage] = useState("");
  const [faceReferenceFile, setFaceReferenceFile] = useState(null);
  const [driverVideo, setDriverVideo] = useState(null);
  const [releaseFile, setReleaseFile] = useState(null);
  const [consent, setConsent] = useState(false);
  const [performerName, setPerformerName] = useState("");
  const [avatarName, setAvatarName] = useState("");
  const [gender, setGender] = useState("female");
  const [submission, setSubmission] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [motionScanning, setMotionScanning] = useState(false);
  const [motionMap, setMotionMap] = useState({ head: 0, arms: 0, torso: 0, hips: 0, legs: 0 });
  const [motionCoverage, setMotionCoverage] = useState({ head: 0, arms: 0, torso: 0, hips: 0, legs: 0 });
  const [poseFit, setPoseFit] = useState({ status: "idle", missing: [] });
  const [qaDemo, setQaDemo] = useState(false);
  const [releaseAttested, setReleaseAttested] = useState(false);
  const [extractingIdentity, setExtractingIdentity] = useState(false);
  const [privatePackages, setPrivatePackages] = useState([]);
  const [privatePackagesStatus, setPrivatePackagesStatus] = useState("loading");
  const [privatePackagesError, setPrivatePackagesError] = useState("");
  const [benchmarkAvatars, setBenchmarkAvatars] = useState([]);
  const [benchmarkAvatarsStatus, setBenchmarkAvatarsStatus] = useState("loading");
  const [benchmarkAvatarsError, setBenchmarkAvatarsError] = useState("");
  const [benchmarkAvatarId, setBenchmarkAvatarId] = useState("");
  const [fullBodyReferenceUrl, setFullBodyReferenceUrl] = useState("");
  const [fullBodyReferenceApproved, setFullBodyReferenceApproved] = useState(false);
  const [fullBodyReferenceFit, setFullBodyReferenceFit] = useState({ status: "idle", missing: [] });
  const [manualSourceReview, setManualSourceReview] = useState({ head: false, hands: false, feet: false, framing: false });
  const [captureClip, setCaptureClip] = useState("frame");
  const [capturedMotionClips, setCapturedMotionClips] = useState({});
  const [cameraPreviewAspect, setCameraPreviewAspect] = useState(16 / 9);
  const [qaJob, setQaJob] = useState(null);
  const [qaScript, setQaScript] = useState("");
  const [qaReview, setQaReview] = useState({ face_identity: 0, lip_sync: 0, motion: 0, framing: 0, artifacts: 0, notes: "" });
  const qaReviewFields = [
    ["face_identity", "Face is sharp and stable"],
    ["lip_sync", "Speech timing and mouth shape"],
    ["motion", "Motion looks natural for this mode"],
    ["framing", "Framing is usable throughout"],
    ["artifacts", "No distracting visual artifacts"],
  ];
  const qaReviewReady = qaReviewFields.every(([key]) => Number(qaReview[key]) >= 4);
  const manualSourceReviewReady = Object.values(manualSourceReview).every(Boolean);
  const sourceReviewReady = fullBodyReferenceFit.status === "full" || manualSourceReviewReady;
  const motionSteps = [
    { key: "head", label: "Head & face", instruction: "Keep your face inside the top frame. Slowly look left, center, then right." },
    { key: "arms", label: "Arms & hands", instruction: "Raise both hands into view and make two natural, open gestures." },
    { key: "torso", label: "Torso", instruction: "Stay square to camera, then make a small natural turn and return to center." },
    { key: "hips", label: "Hips", instruction: "Shift your weight gently from left to right while keeping both hips visible." },
    { key: "legs", label: "Legs & feet", instruction: "Keep knees and feet in frame. Take one small step forward and back." },
  ];
  const nextMotionStep = motionSteps.find((step) => (motionCoverage[step.key] || 0) < 25) || null;
  const hasIdentityForStorage = Boolean(identityImage) || qaDemo;
  const canStorePrivateTest = Boolean(
    consent && performerName.trim() && avatarName.trim() && hasIdentityForStorage && driverVideo && (releaseFile || releaseAttested),
  );
  const storageChecklist = [
    [performerName.trim() && avatarName.trim(), "performer and avatar names"],
    [consent, "written-consent confirmation"],
    [hasIdentityForStorage, "identity frame"],
    [driverVideo, "Natural-walk motion driver"],
    [releaseFile || releaseAttested, "signed release or admin attestation"],
  ];

  async function loadPrivatePackages(retry = 0) {
    if (retry === 0) {
      setPrivatePackagesStatus("loading");
      setPrivatePackagesError("");
    }
    try {
      const response = await api.get("/api/admin/avatar-lab/submissions");
      setPrivatePackages(response.data.items || []);
      setPrivatePackagesStatus("ready");
    } catch (e) {
      // The session token can arrive just after this nested Admin component
      // mounts. Retry before presenting an empty private library.
      if (retry < 8) {
        window.setTimeout(() => loadPrivatePackages(retry + 1), 1200);
      } else {
        setPrivatePackagesStatus("error");
        setPrivatePackagesError(e?.response?.data?.detail || "The private package list could not be loaded. Your stored private media has not been removed.");
      }
    }
  }

  async function loadBenchmarkAvatars(retry = 0) {
    if (retry === 0) {
      setBenchmarkAvatarsStatus("loading");
      setBenchmarkAvatarsError("");
    }
    try {
      const response = await api.get("/api/avatars?limit=100");
      const items = (response.data.items || []).filter((avatar) => avatar.source === "omnimark_generated" && (avatar.avatar_image_url || avatar.image_url || avatar.preview_url));
      setBenchmarkAvatars(items);
      setBenchmarkAvatarId((current) => current && items.some((avatar) => avatar.id === current) ? current : (items[0]?.id || ""));
      setBenchmarkAvatarsStatus("ready");
    } catch (e) {
      // This nested Admin component can mount before the authenticated API
      // client receives its session. Never turn that temporary race into the
      // false claim that no OmniMark-generated avatars exist.
      if (retry < 8) {
        window.setTimeout(() => loadBenchmarkAvatars(retry + 1), 1200);
      } else {
        setBenchmarkAvatars([]);
        setBenchmarkAvatarsStatus("error");
        setBenchmarkAvatarsError(e?.response?.data?.detail || "The generated Avatar Studio list could not be loaded. No avatars were removed.");
      }
    }
  }

  async function loadActiveQaJob(retry = 0) {
    try {
      const response = await api.get("/api/admin/avatar-lab/qa-jobs/active");
      let job = (response.data.items || [])[0] || null;
      // A completed smoke test still needs its human visual review after a
      // refresh. Restore it instead of leaving the only safe next action
      // invisible and encouraging a duplicate smoke render.
      if (!job) {
        const latestResponse = await api.get("/api/admin/avatar-lab/qa-jobs/latest");
        job = latestResponse.data.item || null;
      }
      if (!job) return;
      setQaJob(job);
      if (job.status === "processing") pollQaJob(job.workspace_id, job.id);
    } catch (e) {
      // Match the short authenticated-session retry used by the other admin
      // data loads.  Failing this read must never make a duplicate job safe.
      if (retry < 8) window.setTimeout(() => loadActiveQaJob(retry + 1), 1200);
    }
  }

  async function prepareFullBodyReference({ regenerate = false } = {}) {
    if (!benchmarkAvatarId) {
      setError("Select an OmniMark-generated avatar first.");
      return;
    }
    // This is an admin-only, clearly-labelled reference action. Native browser
    // confirms block the render control itself in embedded browsers, and the
    // server still enforces the admin session and keeps the result in private
    // QA. The deliberate button click is the confirmation; it never starts a
    // GPU video job or releases anything to customers.
    setBusy(true);
    setError("");
    try {
      const response = await api.post(`/api/admin/avatars/${benchmarkAvatarId}/prepare-full-body-reference`, { regenerate });
      const referenceUrl = response.data.full_body_reference_url || "";
      setFullBodyReferenceUrl(referenceUrl);
      setFullBodyReferenceApproved(false);
      setManualSourceReview({ head: false, hands: false, feet: false, framing: false });
      await validateFullBodyReference(referenceUrl);
      setMessage(response.data.message || "Generated full-body reference ready for visual review.");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not prepare a generated full-body reference. No GPU video render was started.");
    } finally {
      setBusy(false);
    }
  }

  async function validateFullBodyReference(referenceUrl) {
    if (!referenceUrl) {
      setFullBodyReferenceFit({ status: "unavailable", missing: [] });
      return;
    }
    setFullBodyReferenceFit({ status: "loading", missing: [] });
    let detector;
    try {
      const image = new Image();
      image.crossOrigin = "anonymous";
      await new Promise((resolve, reject) => {
        image.onload = resolve;
        image.onerror = reject;
        image.src = `${referenceUrl}${referenceUrl.includes("?") ? "&" : "?"}pose-review=${Date.now()}`;
      });
      const vision = await FilesetResolver.forVisionTasks(POSE_WASM_ROOT);
      detector = await PoseLandmarker.createFromOptions(vision, {
        baseOptions: { modelAssetPath: POSE_MODEL_URL },
        runningMode: "IMAGE",
        numPoses: 1,
        minPoseDetectionConfidence: 0.55,
        minPosePresenceConfidence: 0.55,
      });
      const landmarks = detector.detect(image).landmarks?.[0];
      if (!landmarks) {
        setFullBodyReferenceFit({ status: "not-full", missing: ["a visible full person"] });
        return;
      }
      const visible = (index) => {
        const point = landmarks[index];
        return Boolean(point && (point.visibility ?? 1) >= 0.55 && (point.presence ?? 1) >= 0.55 && point.x > .02 && point.x < .98 && point.y > .02 && point.y < .98);
      };
      const checks = { head: [0, 2, 5], hands: [15, 16], hips: [23, 24], legs: [25, 26], feet: [27, 28, 31, 32] };
      const missing = Object.entries(checks).filter(([, indices]) => !indices.some(visible)).map(([name]) => name);
      setFullBodyReferenceFit({ status: missing.length ? "not-full" : "full", missing });
    } catch {
      setFullBodyReferenceFit({ status: "unavailable", missing: [] });
    } finally {
      detector?.close?.();
    }
  }

  async function approveFullBodyReference() {
    if (!benchmarkAvatarId || !fullBodyReferenceUrl) {
      setError("Prepare and open the generated full-body reference before approving it.");
      return;
    }
    if (!sourceReviewReady) {
      setError("Complete the on-device framing check, or review the inline source and confirm every head-to-toe item before approval.");
      return;
    }
    // The labelled admin-only control is the confirmation. Native confirm dialogs
    // cannot be completed reliably in embedded browsers, while the server still
    // requires the authenticated admin session and only unlocks private QA.
    setBusy(true);
    setError("");
    try {
      const response = await api.post(`/api/admin/avatars/${benchmarkAvatarId}/approve-full-body-reference`);
      setFullBodyReferenceApproved(true);
      setMessage(response.data.message || "Full-body reference approved for private QA.");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not record the full-body reference review.");
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => () => {
    if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    if (motionIntervalRef.current) window.clearInterval(motionIntervalRef.current);
    if (poseIntervalRef.current) window.clearInterval(poseIntervalRef.current);
    poseLandmarkerRef.current?.close?.();
    recorderRef.current?.stop?.();
    streamRef.current?.getTracks()?.forEach((track) => track.stop());
  }, []);

  useEffect(() => {
    loadPrivatePackages();
    loadBenchmarkAvatars();
    loadActiveQaJob();
    // These initial admin snapshots intentionally load once. Each helper reads
    // the authenticated session and must not restart camera/QA state on render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The preview element is conditionally rendered. On the first camera press
  // it does not exist until React applies the ready state, so attach the stream
  // again after that render. Without this, some phones grant camera access but
  // show a black preview and cannot start a guided recording.
  useEffect(() => {
    const video = videoRef.current;
    const stream = streamRef.current;
    if (!video || !stream || video.srcObject === stream) return;
    video.srcObject = stream;
    video.play?.().catch(() => undefined);
  }, [cameraState]);

  async function startCamera() {
    if (streamRef.current) return streamRef.current;
    setError("");
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error("camera API unavailable");
      }
      // Full-body capture does not need audio. Requesting only a front-facing
      // camera avoids a combined mic prompt and works on more phone/tablet
      // browsers; users can upload a separate narrated clip when needed.
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          // Do not force a portrait aspect ratio. On iPhone that can crop the
          // native camera sensor and make the performer appear artificially
          // zoomed-in. Use the wide native stream; the preview adapts to it.
          video: { facingMode: { ideal: "user" }, width: { ideal: 1280 }, height: { ideal: 720 }, resizeMode: { ideal: "none" }, frameRate: { ideal: 24, max: 30 } },
          audio: false,
        });
      } catch {
        // Some iOS/iPadOS browsers reject detailed constraints even though a
        // camera is available. Retry with the broadest camera-only request.
        stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      }
      // Some mobile cameras expose an optical/digital zoom capability. Select
      // the widest permitted setting instead of inheriting a prior zoom state.
      const track = stream.getVideoTracks()[0];
      const capabilities = track?.getCapabilities?.();
      if (capabilities?.zoom?.min !== undefined) {
        await track.applyConstraints?.({ advanced: [{ zoom: capabilities.zoom.min }] }).catch(() => undefined);
      }
      const settings = track?.getSettings?.(); if (settings?.width && settings?.height) setCameraPreviewAspect(settings.width / settings.height); streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play?.().catch(() => undefined);
      }
      setCameraState("ready");
      setMessage("Camera started at the widest available view. Turn the phone sideways and step back for a full-body capture.");
      startMotionMap();
      initializePoseLandmarker();
      return stream;
    } catch {
      setCameraState("idle");
      setError("Camera access was unavailable. On phone or tablet, open Admin in Safari, Chrome, or Edge over HTTPS, allow Camera, then try again. Microphone access is not required for body-motion capture.");
      return null;
    }
  }

  function stopCamera() {
    if (recorderRef.current?.state === "recording") recorderRef.current.stop();
    if (motionIntervalRef.current) window.clearInterval(motionIntervalRef.current);
    if (poseIntervalRef.current) window.clearInterval(poseIntervalRef.current);
    motionIntervalRef.current = null;
    poseIntervalRef.current = null;
    previousMotionFrameRef.current = null;
    poseLandmarkerRef.current?.close?.();
    poseLandmarkerRef.current = null;
    setPoseFit({ status: "idle", missing: [] });
    streamRef.current?.getTracks()?.forEach((track) => track.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraPreviewAspect(16 / 9);
    setMotionScanning(false);
    setCameraState("idle");
  }

  function sampleMotionMap() {
    const video = videoRef.current;
    if (!video?.videoWidth || !video?.videoHeight) return;
    const width = 192;
    const height = Math.max(128, Math.round((video.videoHeight / video.videoWidth) * width));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d", { willReadFrequently: true });
    context.drawImage(video, 0, 0, width, height);
    const frame = context.getImageData(0, 0, width, height).data;
    const prior = previousMotionFrameRef.current;
    previousMotionFrameRef.current = new Uint8ClampedArray(frame);
    if (!prior) return;

    // These guide zones deliberately measure movement coverage, not identity, pose, or biometrics.
    const zones = {
      head: [0.32, 0.06, 0.68, 0.25], arms: [0.08, 0.24, 0.92, 0.55],
      torso: [0.30, 0.25, 0.70, 0.58], hips: [0.30, 0.58, 0.70, 0.72], legs: [0.22, 0.72, 0.78, 0.98],
    };
    const next = {};
    Object.entries(zones).forEach(([name, [left, top, right, bottom]]) => {
      let difference = 0;
      let samples = 0;
      for (let y = Math.floor(top * height); y < Math.floor(bottom * height); y += 4) {
        for (let x = Math.floor(left * width); x < Math.floor(right * width); x += 4) {
          const index = (y * width + x) * 4;
          difference += Math.abs(frame[index] - prior[index]) + Math.abs(frame[index + 1] - prior[index + 1]) + Math.abs(frame[index + 2] - prior[index + 2]);
          samples += 1;
        }
      }
      next[name] = Math.min(100, Math.round((difference / Math.max(samples, 1) / 2.1)));
    });
    setMotionMap(next);
    setMotionCoverage((current) => Object.fromEntries(Object.entries(next).map(([zone, score]) => [zone, Math.max(current[zone] || 0, score)])));
  }

  function startMotionMap() {
    if (motionIntervalRef.current) window.clearInterval(motionIntervalRef.current);
    previousMotionFrameRef.current = null;
    setMotionMap({ head: 0, arms: 0, torso: 0, hips: 0, legs: 0 });
    setMotionCoverage({ head: 0, arms: 0, torso: 0, hips: 0, legs: 0 });
    setMotionScanning(true);
    motionIntervalRef.current = window.setInterval(sampleMotionMap, 250);
  }

  async function initializePoseLandmarker() {
    if (poseLandmarkerRef.current) return;
    setPoseFit({ status: "loading", missing: [] });
    try {
      const vision = await FilesetResolver.forVisionTasks(POSE_WASM_ROOT);
      const landmarker = await PoseLandmarker.createFromOptions(vision, {
        baseOptions: { modelAssetPath: POSE_MODEL_URL },
        runningMode: "VIDEO",
        numPoses: 1,
        minPoseDetectionConfidence: 0.55,
        minPosePresenceConfidence: 0.55,
        minTrackingConfidence: 0.55,
      });
      poseLandmarkerRef.current = landmarker;
      if (poseIntervalRef.current) window.clearInterval(poseIntervalRef.current);
      poseIntervalRef.current = window.setInterval(samplePoseFit, 700);
      samplePoseFit();
    } catch {
      setPoseFit({ status: "unavailable", missing: [] });
      setError("Full-body detection could not start on this device. Check your connection, then reopen the camera. No camera video was uploaded.");
    }
  }

  function samplePoseFit() {
    const video = videoRef.current;
    const landmarker = poseLandmarkerRef.current;
    if (!video?.videoWidth || !landmarker) return;
    try {
      const landmarks = landmarker.detectForVideo(video, performance.now()).landmarks?.[0];
      if (!landmarks) {
        setPoseFit({ status: "not-full", missing: ["a visible person"] });
        return;
      }
      const isVisible = (index) => {
        const landmark = landmarks[index];
        return Boolean(landmark && (landmark.visibility ?? 1) >= 0.55 && (landmark.presence ?? 1) >= 0.55);
      };
      const checks = {
        head: [0, 2, 5], arms: [15, 16], hips: [23, 24], legs: [25, 26], feet: [27, 28, 31, 32],
      };
      const missing = Object.entries(checks).filter(([, indices]) => !indices.some(isVisible)).map(([name]) => name);
      setPoseFit({ status: missing.length ? "not-full" : "full", missing });
    } catch {
      setPoseFit({ status: "unavailable", missing: [] });
    }
  }

  function captureIdentity() {
    const video = videoRef.current;
    if (!video?.videoWidth) {
      setError("Start the camera and wait for the live preview before capturing the avatar identity.");
      return;
    }
    const scale = Math.min(1, 1280 / Math.max(video.videoWidth, video.videoHeight));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    setIdentityImage(canvas.toDataURL("image/jpeg", 0.9));
    setMessage("Identity frame captured. Keep the performer full body and clearly visible for the best motion-transfer test.");
  }

  function renderIdentityFrame(source, width, height, label) {
    const scale = Math.min(1, 1280 / Math.max(width, height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(width * scale);
    canvas.height = Math.round(height * scale);
    canvas.getContext("2d").drawImage(source, 0, 0, canvas.width, canvas.height);
    setIdentityImage(canvas.toDataURL("image/jpeg", 0.9));
    setMessage(`${label} was captured as the private identity frame. You can replace it with a camera capture before storing the package.`);
  }

  function selectIdentityImage(event) {
    const file = event.target.files?.[0] || null;
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || file.size > 8 * 1024 * 1024) {
      setError("Use a JPG, PNG, or WebP identity image under 8 MB.");
      event.target.value = "";
      return;
    }
    const image = new Image();
    const url = URL.createObjectURL(file);
    image.onload = () => {
      try {
        renderIdentityFrame(image, image.naturalWidth, image.naturalHeight, "Uploaded image");
      } finally {
        URL.revokeObjectURL(url);
      }
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      setError("The uploaded identity image could not be read.");
    };
    image.src = url;
  }

  function selectFaceReference(event) {
    const file = event.target.files?.[0] || null;
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || file.size > 8 * 1024 * 1024) {
      setError("Use a JPG, PNG, or WebP close face reference under 8 MB.");
      event.target.value = "";
      return;
    }
    setFaceReferenceFile(file);
    setMessage("Close face reference selected. It stays private and is used only for speech and lip-sync quality.");
  }

  async function captureIdentityFromVideo(file) {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.muted = true;
    video.playsInline = true;
    video.preload = "auto";
    try {
      setExtractingIdentity(true);
      await new Promise((resolve, reject) => {
        const timer = window.setTimeout(() => reject(new Error("video load timed out")), 6000);
        video.onloadeddata = () => { window.clearTimeout(timer); resolve(); };
        video.onerror = () => { window.clearTimeout(timer); reject(new Error("video unreadable")); };
        video.src = url;
      });
      const duration = Number(video.duration);
      const sampleAt = Number.isFinite(duration) && duration > 0.8
        ? Math.min(Math.max(0.35, duration * 0.08), Math.max(0.35, duration - 0.2))
        : null;
      if (sampleAt !== null) {
        await new Promise((resolve) => {
          const timer = window.setTimeout(resolve, 2200);
          video.onseeked = () => { window.clearTimeout(timer); resolve(); };
          video.currentTime = sampleAt;
        });
      }
      if (!video.videoWidth || !video.videoHeight) throw new Error("no video frame");
      renderIdentityFrame(video, video.videoWidth, video.videoHeight, "A frame from the uploaded video");
    } catch {
      setError("");
      setMessage("This browser cannot preview an identity frame from the demo, but OmniMark will extract the private frame on the server when you store the QA demo.");
    } finally {
      setExtractingIdentity(false);
      video.removeAttribute("src");
      video.load();
      URL.revokeObjectURL(url);
    }
  }

  async function startDriverCapture() {
    // One press should start the camera and record the selected guided step.
    // Pose detection is a helpful visual signal, not a device-specific gate.
    const stream = streamRef.current || await startCamera();
    if (!stream) return;
    if (poseFit.status === "not-full") setMessage(`Recording is allowed, but step back before the next clip: automatic framing still needs ${poseFit.missing.join(", ")}.`);
    if (poseFit.status === "unavailable") setMessage("Automatic framing is unavailable on this device. Recording is allowed; use the live preview to keep your full body in frame.");
    if (!window.MediaRecorder) {
      setError("This browser cannot record a motion driver. Use a current browser on a camera-equipped device.");
      return;
    }
    const chunks = [];
    const supportedMimeType = [
      "video/mp4;codecs=avc1,mp4a",
      "video/mp4",
      "video/webm;codecs=vp9,opus",
      "video/webm;codecs=vp8,opus",
      "video/webm",
    ].find((mimeType) => MediaRecorder.isTypeSupported(mimeType));
    if (!supportedMimeType) {
      setError("This browser does not provide a compatible camera recording format. Use the current Safari, Chrome, or Edge browser, or upload a short MP4 instead.");
      return;
    }
    let recorder;
    try {
      recorder = new MediaRecorder(stream, {
        mimeType: supportedMimeType,
        videoBitsPerSecond: 1_800_000,
        audioBitsPerSecond: 64_000,
      });
    } catch {
      setError("The phone could not start its camera recorder. Please try again, or upload a short MP4 performance video instead.");
      return;
    }
    recorder.ondataavailable = (event) => { if (event.data?.size) chunks.push(event.data); };
    recorder.onerror = () => {
      setError("The phone camera recording stopped unexpectedly. Please record again, or upload a short MP4 performance video instead.");
      setCameraState("ready");
      recorderRef.current = null;
    };
    const selectedClip = BODY_CAPTURE_CLIPS.find((clip) => clip.id === captureClip) || BODY_CAPTURE_CLIPS[0];
    recorder.onstop = () => {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
      const recording = new Blob(chunks, { type: recorder.mimeType || supportedMimeType });
      if (!recording.size) {
        setDriverVideo(null);
        setError("The phone did not return a usable recording. Please try the 15-second capture again after the live preview is visible, or upload a short MP4 performance video.");
        setCameraState("ready");
        recorderRef.current = null;
        return;
      }
      if (recording.size > MAX_PRIVATE_DRIVER_BYTES) {
        setDriverVideo(null);
        setError("The camera recording is too large to save safely. Record again using the same 15-second guide; OmniMark will use the lower-bandwidth camera setting automatically.");
        setCameraState("ready");
        recorderRef.current = null;
        return;
      }
      setCapturedMotionClips((current) => ({ ...current, [selectedClip.id]: recording }));
      // The natural-walk clip is the only current candidate for the private
      // motion-driver upload. Other clips remain local until a future verified
      // multi-clip renderer is available.
      if (selectedClip.id === "walk") setDriverVideo(recording);
      const completedIndex = BODY_CAPTURE_CLIPS.findIndex((clip) => clip.id === selectedClip.id);
      const nextClip = BODY_CAPTURE_CLIPS.slice(completedIndex + 1).find((clip) => !capturedMotionClips[clip.id]);
      if (nextClip) setCaptureClip(nextClip.id);
      setCameraState("ready");
      setMessage(`${selectedClip.label} captured (${(recording.size / 1024 / 1024).toFixed(1)} MB). ${selectedClip.id === "walk" ? "It is selected as the private motion-driver candidate." : nextClip ? `Next: ${nextClip.label}.` : "All guided local movement clips are complete."}`);
      recorderRef.current = null;
    };
    recorderRef.current = recorder;
    try {
      recorder.start();
      setCameraState("recording");
      setMessage(`Recording ${selectedClip.label.toLowerCase()} for ${selectedClip.seconds} seconds… keep your full body in the live preview.`);
    } catch {
      setError("The phone could not begin recording. Please try again after the live camera preview appears, or upload a short MP4 performance video instead.");
      recorderRef.current = null;
      return;
    }
    timeoutRef.current = window.setTimeout(() => recorder.state === "recording" && recorder.stop(), selectedClip.seconds * 1000);
  }

  function stopDriverCapture() {
    if (recorderRef.current?.state === "recording") recorderRef.current.stop();
  }

  async function selectDriverVideo(event) {
    const file = event.target.files?.[0] || null;
    if (!file) return;
    if (!isSupportedPrivateVideo(file) || file.size > MAX_PRIVATE_DRIVER_BYTES) {
      setError("Use an MP4, WebM, or MOV performance clip under 120 MB. Up to 60 seconds is accepted for private source review; a short 720p clip remains best for motion QA.");
      event.target.value = "";
      return;
    }
    setDriverVideo(file);
    setCapturedMotionClips((current) => ({ ...current, walk: file }));
    setQaDemo(true);
    const shortName = file.name.replace(/\.[^.]+$/, "").replace(/[-_]+/g, " ").trim() || "Admin performance demo";
    setPerformerName((current) => current || "Admin demo performer");
    setAvatarName((current) => current || `${shortName} QA`);
    setError("");
    setMessage(`${file.name} is ready as the private performance driver. OmniMark is extracting an identity frame now.`);
    await captureIdentityFromVideo(file);
  }

  async function submitTest() {
    if (extractingIdentity) {
      setError("OmniMark is still extracting the identity frame from the uploaded video. Please wait a moment.");
      return;
    }
    if (!consent || !performerName.trim() || !avatarName.trim() || (!identityImage && !qaDemo) || !driverVideo || (!releaseFile && !releaseAttested)) {
      setError("Confirm consent, then add an identity frame and motion driver. Private QA demos can derive the identity frame from the uploaded video. For the release, upload the file or confirm that the signed release is retained offline.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const response = await api.post("/api/admin/avatar-lab/submissions", {
        name: avatarName.trim(), performer_name: performerName.trim(), gender,
        consent_confirmed: true, identity_image_data_url: identityImage || null,
        face_reference_image_data_url: faceReferenceFile ? await dataUrlFor(faceReferenceFile) : null,
        performance_video_data_url: await dataUrlFor(driverVideo),
        signed_release_data_url: releaseFile ? await dataUrlFor(releaseFile) : null, signed_release_name: releaseFile?.name || "admin-offline-release-attestation.txt",
        look_style: "rights-cleared in-house human performance reference, natural commercial presenter",
        personality: "natural, credible, expressive", speaking_style: "clear, conversational",
        camera_presence: "full-body motion-transfer reference",
        qa_demo: qaDemo, release_attested: releaseAttested,
      });
      setSubmission(response.data.submission);
      setMessage(response.data.message || "Private test package stored for review.");
      await loadPrivatePackages();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not store the private test package.");
    } finally {
      setBusy(false);
    }
  }

  async function attachFaceReference(item, event) {
    const file = event.target.files?.[0] || null;
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || file.size > 8 * 1024 * 1024) {
      setError("Use a JPG, PNG, or WebP close face reference under 8 MB.");
      event.target.value = "";
      return;
    }
    setBusy(true);
    setError("");
    try {
      const response = await api.post(`/api/admin/avatar-lab/submissions/${item.id}/face-reference`, {
        face_reference_image_data_url: await dataUrlFor(file),
      });
      setMessage(response.data.message || "Private close face reference attached.");
      await loadPrivatePackages();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not attach the private close face reference.");
    } finally {
      setBusy(false);
      event.target.value = "";
    }
  }

  async function pollQaJob(workspaceId, jobId) {
    try {
      const response = await api.get(`/api/workspaces/${workspaceId}/avatar/jobs/${jobId}`);
      const job = response.data;
      setQaJob(job);
      if (job.status === "processing") {
        window.setTimeout(() => pollQaJob(workspaceId, jobId), 10000);
      } else if (job.status === "completed") {
        setMessage("Private QA render completed. Review the video, then explicitly approve the smoke result before any 30-second QA review.");
      } else {
        setError(job?.output?.error || "The private QA render did not pass. No credits were charged.");
      }
    } catch {
      window.setTimeout(() => pollQaJob(workspaceId, jobId), 15000);
    }
  }

  async function startQaRender(item, renderMode = "face_motion_reference", performanceProfile = "natural_presenter", motionDriverSource = "installed_verified_motion") {
    const isFullBody = renderMode === "full_body"; const usesPrivateCapture = isFullBody && motionDriverSource === "private_capture_experimental";
    if (!benchmarkAvatarId) {
      setError("Create an OmniMark-generated Avatar Studio avatar before starting a private performance benchmark.");
      return;
    }
    if (!isFullBody && !item.face_reference_attached) {
      setError("Attach a well-lit close face reference first. OmniMark will not start a face-and-head-motion QA render from a full-body identity frame because that is not reliable enough for premium lip-sync.");
      return;
    }
    const confirmation = isFullBody
      ? `Start one zero-credit, 4-second private ${performanceProfile.replaceAll("_", "-")} full-body motion smoke render? ${usesPrivateCapture ? "It will use this specific consented private performance capture as the experimental motion driver after the automated hand-pose preflight." : "It will use OmniMark's installed verified motion driver."} Raw media remains private.`
      : "Start one zero-credit, 4-second private Echo portrait smoke render? OmniMark will use Echo's generated face reference and OmniMark's synthetic performance driver. The private package remains a read-only visual reference; it is not used for identity or direct motion transfer. This is not full-body transfer.";
    if (!window.confirm(confirmation)) return;
    setBusy(true);
    setError("");
    try {
      const request = { render_mode: renderMode, target_duration_seconds: 4, benchmark_avatar_id: benchmarkAvatarId, performance_profile: performanceProfile, motion_driver_source: motionDriverSource };
      if (qaScript.trim()) request.script = qaScript.trim();
      const response = await api.post(`/api/admin/avatar-lab/submissions/${item.id}/render-qa`, request);
      const job = response.data;
      setQaJob(job);
      setMessage(response.data.message || `Private 4-second ${isFullBody ? "full-body" : "portrait"} smoke render queued. It uses zero credits.`);
      pollQaJob(job.workspace_id, job.id);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not start the private QA render. No credits were charged.");
    } finally {
      setBusy(false);
    }
  }

  async function approveQaVisualReview() {
    if (!qaJob?.id || qaJob?.status !== "completed") return;
    const isSmokeReview = Number(qaJob?.input?.target_duration_seconds) === 4;
    if (!qaReviewReady) {
      setError(isSmokeReview
        ? "Record every visual-review score at 4 or 5 before approving this private smoke result. A score below 4 keeps it blocked from a 30-second QA render."
        : "Record every visual-review score at 4 or 5 before recording the final private 30-second QA review.");
      return;
    }
    const confirmation = isSmokeReview
      ? "Confirm these recorded visual-review scores. This permits only a longer private QA review; it does not publish the avatar to customers."
      : "Confirm these recorded visual-review scores for the private 30-second QA review. This does not publish the avatar to customers.";
    if (!window.confirm(confirmation)) return;
    setBusy(true);
    setError("");
    try {
      const response = await api.post(`/api/admin/avatar-lab/qa-jobs/${qaJob.id}/approve-visual`, {
        visual_review: {
          scores: Object.fromEntries(qaReviewFields.map(([key]) => [key, Number(qaReview[key])])),
          notes: qaReview.notes.trim(),
        },
      });
      setQaJob((current) => ({ ...current, output: { ...(current?.output || {}), quality_gate_passed: true, visual_review_status: "admin_approved", visual_review: { scores: Object.fromEntries(qaReviewFields.map(([key]) => [key, Number(qaReview[key])])), notes: qaReview.notes.trim() } } }));
      setMessage(response.data.message || (isSmokeReview
        ? "Private smoke result approved for a 30-second QA review."
        : "Private 30-second QA review recorded. This does not approve the avatar for customers."));
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not record the private visual review.");
    } finally {
      setBusy(false);
    }
  }

  async function startThirtySecondQaReview() {
    const submissionId = qaJob?.input?.admin_avatar_lab_submission_id;
    const avatarId = qaJob?.input?.benchmark_avatar_id || benchmarkAvatarId;
    if (!submissionId || !avatarId || qaJob?.output?.visual_review_status !== "admin_approved") {
      setError("A completed 4-second face-and-head-motion smoke test with a recorded passing visual review is required before a 30-second QA review.");
      return;
    }
    if (!window.confirm("Start one zero-credit, private 30-second portrait QA review? This remains an internal OmniMark-generated-avatar test and is not published to customers.")) return;
    setBusy(true);
    setError("");
    try {
      const response = await api.post(`/api/admin/avatar-lab/submissions/${submissionId}/render-qa`, {
        render_mode: "face_motion_reference",
        target_duration_seconds: 30,
        benchmark_avatar_id: avatarId,
        performance_profile: "natural_presenter",
        ...(qaScript.trim() ? { script: qaScript.trim() } : {}),
      });
      setQaJob(response.data);
      setMessage(response.data.message || "Private 30-second portrait QA review queued. It uses zero credits and remains unpublished.");
      pollQaJob(response.data.workspace_id, response.data.id);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not start the private 30-second QA review. No credits were charged.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section style={{ ...card, marginTop: 24, borderColor: "#a7f3d0", background: "#f0fdf4" }} data-testid="avatar-performance-lab">
      <div style={{ fontSize: 18, fontWeight: 900 }}>Avatar Performance Lab</div>
      <div style={{ color: "#166534", marginTop: 6, maxWidth: 900 }}>
        Admin-only performance-reference testing. Capture or upload a consented MP4, WebM, or MOV demo to evaluate movement and speech timing against a separate OmniMark-generated avatar. The demo remains private, read-only, and never becomes the visible customer avatar.
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 10, marginTop: 14 }}>
        <input value={performerName} onChange={(event) => setPerformerName(event.target.value)} placeholder="Performer legal name or internal ID" style={selectStyle} />
        <input value={avatarName} onChange={(event) => setAvatarName(event.target.value)} placeholder="Avatar name for Avatar Studio" style={selectStyle} />
        <select value={gender} onChange={(event) => setGender(event.target.value)} style={selectStyle}><option value="female">Female voice presentation</option><option value="male">Male voice presentation</option></select>
      </div>
      <label style={{ display: "block", marginTop: 12, fontSize: 13 }}><input type="checkbox" checked={consent} onChange={(event) => setConsent(event.target.checked)} /> I verified written consent for AI performance transfer and customer advertising outputs.</label>
      <label style={{ display: "block", marginTop: 10, fontSize: 13 }}>Signed release (PDF, PNG, or JPG)<input type="file" accept="application/pdf,image/png,image/jpeg" onChange={(event) => setReleaseFile(event.target.files?.[0] || null)} style={{ display: "block", marginTop: 5 }} /></label>
      <label style={{ display: "block", marginTop: 8, fontSize: 12, color: "#166534" }}><input type="checkbox" checked={releaseAttested} onChange={(event) => setReleaseAttested(event.target.checked)} /> I am the authorized admin and retain the performer’s signed release offline. Record an admin attestation instead of uploading the duplicate document.</label>
      <label style={{ display: "block", marginTop: 10, fontSize: 13 }}>Upload identity image (JPG, PNG, or WebP; optional if a demo video is uploaded)<input type="file" accept="image/jpeg,image/png,image/webp" onChange={selectIdentityImage} style={{ display: "block", marginTop: 5 }} /></label>
      <label style={{ display: "block", marginTop: 10, fontSize: 13 }}>Close face reference for premium speech (recommended: well-lit head-and-shoulders JPG/PNG/WebP, at least 720×720)<input type="file" accept="image/jpeg,image/png,image/webp" onChange={selectFaceReference} style={{ display: "block", marginTop: 5 }} /></label>
      <label style={{ display: "block", marginTop: 10, fontSize: 13 }}>Upload existing private performance demo (MP4, WebM, or MOV; max 120 MB / 60 seconds)<input type="file" accept="video/mp4,video/webm,video/quicktime,.mov" onChange={selectDriverVideo} style={{ display: "block", marginTop: 5 }} /></label>
      <div style={{ marginTop: 12, padding: 10, borderRadius: 9, background: "#dcfce7", border: "1px solid #86efac", color: "#166534", fontSize: 13 }}>
        <b>Guided private source capture.</b> Complete the seven body-part steps below in order. They stay on this device for review; only Natural walk becomes the private QA driver. To create a private test, capture an identity frame and add the release confirmation.
        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 10px", marginTop: 6 }}>{storageChecklist.map(([complete, label]) => <span key={label}>{complete ? "✓" : "○"} {label}</span>)}</div>
      </div>
      <div style={{ marginTop: 12, padding: 10, borderRadius: 10, background: "#f8fafc", border: "1px solid #cbd5e1" }}>
        <div style={{ fontWeight: 800, fontSize: 13 }}>Step-by-step body movement capture</div>
        <div style={{ marginTop: 4, fontSize: 12, color: "#475569" }}>Work from head to feet. Each clip stays on this device; only Natural walk is selected as a private QA motion driver.</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 8, marginTop: 9 }}>
          {BODY_CAPTURE_CLIPS.map((clip) => <button key={clip.id} type="button" onClick={() => setCaptureClip(clip.id)} disabled={cameraState === "recording" || busy} style={{ ...btnSmall, minHeight: 58, textAlign: "left", borderColor: captureClip === clip.id ? "#2563eb" : "rgba(0,0,0,.12)", background: captureClip === clip.id ? "#eff6ff" : "#fff" }}><b>{capturedMotionClips[clip.id] ? "✓ " : ""}{clip.label}</b><span style={{ display: "block", marginTop: 3, color: "#64748b", fontWeight: 400, fontSize: 11 }}>{clip.bodyPart} · {clip.seconds} sec</span></button>)}
        </div>
        <div style={{ marginTop: 8, fontSize: 12, color: "#1e3a8a" }}><b>Current step {BODY_CAPTURE_CLIPS.findIndex((clip) => clip.id === captureClip) + 1} of {BODY_CAPTURE_CLIPS.length}:</b> {(BODY_CAPTURE_CLIPS.find((clip) => clip.id === captureClip) || BODY_CAPTURE_CLIPS[0]).instruction}</div>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 }}>
        <button onClick={startCamera} disabled={busy || cameraState !== "idle"} style={{ ...btnSmall, minHeight: 44 }}>Start full-body camera</button>
        {cameraState !== "idle" && <button onClick={stopCamera} disabled={busy} style={btnSmall}>Stop camera</button>}
        {cameraState !== "idle" && <button onClick={startMotionMap} disabled={busy} style={btnSmall}>{motionScanning ? "Restart framing check" : "Start framing check"}</button>}
        {cameraState !== "idle" && <button onClick={captureIdentity} disabled={busy} style={btnSmall}>{identityImage ? "Replace identity frame" : "Capture identity frame"}</button>}
        {cameraState === "recording" ? <button onClick={stopDriverCapture} style={{ ...btnSmall, minHeight: 44 }}>Stop {captureClip} capture</button> : <button onClick={startDriverCapture} disabled={busy} style={{ ...btnSmall, minHeight: 44 }}>{cameraState === "idle" ? `Start camera + record ${captureClip}` : `Record ${captureClip} clip`}</button>}
        <button onClick={submitTest} disabled={busy || !canStorePrivateTest || submission?.status === "approved"} title={canStorePrivateTest ? "Store the private test package" : "Complete every item in the save checklist first."} style={{ ...btnSmall, background: "#111827", color: "#fff", opacity: canStorePrivateTest ? 1 : .55 }}>{busy ? "Working…" : "Store private test"}</button>
      </div>
      {cameraState !== "idle" && <div style={{ marginTop: 14 }}>
        <div style={{ marginBottom: 8, color: "#166534", fontSize: 13 }}><b>Full-body framing:</b> the preview fits the camera's actual aspect ratio without zooming or cropping. Step back until you can visually see head, hands, hips, knees, and feet.</div>
        <div style={{ width: `min(100%, ${72 * cameraPreviewAspect}svh)`, marginInline: "auto", aspectRatio: cameraPreviewAspect, borderRadius: 12, background: "#111827", overflow: "hidden", padding: 0 }}>
          <div style={{ position: "relative", width: "100%", height: "100%", overflow: "hidden", background: "#111827" }}>
        <video ref={videoRef} autoPlay muted playsInline onLoadedMetadata={(event) => {
          const { videoWidth, videoHeight } = event.currentTarget;
          if (videoWidth && videoHeight) setCameraPreviewAspect(videoWidth / videoHeight);
        }} style={{ width: "100%", height: "100%", display: "block", objectFit: "contain", background: "#111827" }} />
          </div>
        </div>
        {cameraPreviewAspect > 1 && <div style={{ marginTop: 7, padding: 8, borderRadius: 8, background: "#eff6ff", color: "#1e3a8a", fontSize: 12 }}><b>Landscape camera feed detected.</b> The preview now fits it without cropping. For a head-to-toe body clip, turn the phone sideways and step back until your full body is visible.</div>}
        <div style={{ marginTop: 8, padding: 10, borderRadius: 8, background: "#064e3b", color: "#ecfdf5", fontSize: 13 }}>{nextMotionStep ? <><b>Next: {nextMotionStep.label}.</b> {nextMotionStep.instruction}</> : <><b>Motion check recorded.</b> It confirms movement only—please visually confirm the full body is in frame before recording the 15-second driver.</>}</div>
        <div style={{ marginTop: 8, padding: 10, borderRadius: 8, background: poseFit.status === "full" ? "#dcfce7" : "#fef3c7", border: `1px solid ${poseFit.status === "full" ? "#86efac" : "#fcd34d"}`, color: poseFit.status === "full" ? "#166534" : "#92400e", fontSize: 13 }}>
          {poseFit.status === "loading" && <><b>Loading full-body detector…</b> This runs locally on the phone camera feed.</>}
          {poseFit.status === "full" && <><b>✓ Full body detected.</b> Head, arms, hips, legs, and feet are visible. You can record the 15-second driver.</>}
          {poseFit.status === "not-full" && <><b>Step back or reframe.</b> Still needed: {poseFit.missing.join(", ")}.</>}
          {poseFit.status === "unavailable" && <><b>Automatic detector unavailable.</b> The live preview still works. Keep head, hands, hips, knees, and feet visible, then use the manual source-review checklist below before approving a generated source.</>}
          {poseFit.status === "idle" && <>Start the camera to run the on-device full-body detector.</>}
        </div>
      </div>}
      {motionScanning && <div style={{ marginTop: 12, padding: 10, borderRadius: 10, background: "#ecfdf5", border: "1px solid #86efac" }}>
        <div style={{ fontWeight: 700, fontSize: 13 }}>Guided Motion Map — {nextMotionStep ? `step ${motionSteps.findIndex((step) => step.key === nextMotionStep.key) + 1} of ${motionSteps.length}` : "complete"}</div>
        <div style={{ marginTop: 4, color: "#166534", fontSize: 12 }}>Move only the highlighted zone first. A green check means movement was observed in that screen area; it does not identify a person or certify that the entire body is visible.</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(90px, 1fr))", gap: 7, marginTop: 9 }}>{motionSteps.map((step) => { const score = motionCoverage[step.key] || 0; return <div key={step.key} style={{ fontSize: 12 }}><div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ textTransform: "capitalize" }}>{step.key}</span><b>{score >= 25 ? "✓" : `${score}%`}</b></div><div style={{ height: 7, borderRadius: 99, background: "#d1fae5", marginTop: 3 }}><div style={{ width: `${score}%`, height: "100%", borderRadius: 99, background: score >= 25 ? "#059669" : nextMotionStep?.key === step.key ? "#f59e0b" : "#94a3b8" }} /></div></div>; })}</div>
      </div>}
      {extractingIdentity && <div style={{ marginTop: 10, color: "#166534", fontSize: 12 }}>Extracting the identity frame from your demo video…</div>}
      {identityImage && <img src={identityImage} alt="Private identity frame preview" style={{ display: "block", marginTop: 12, width: 160, height: 210, objectFit: "cover", borderRadius: 10, border: "1px solid #86efac" }} />}
      {qaDemo && <div style={{ marginTop: 10, color: "#166534", fontSize: 12 }}>QA demo mode: zero credits, private review only. OmniMark uses its own generated avatar as the visible benchmark subject.</div>}
      <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid #bbf7d0" }}>
        <div style={{ fontWeight: 800, fontSize: 13 }}>Private test packages</div>
        <div style={{ marginTop: 4, color: "#166534", fontSize: 12 }}>Stored securely for admin review. Close face references and performance drivers are evaluated privately; raw videos, identity frames, and releases never appear in Avatar Studio or customer pages.</div>
        <label style={{ display: "block", marginTop: 9, fontSize: 12, color: "#166534" }}>OmniMark avatar to benchmark
          <select value={benchmarkAvatarId} onChange={(event) => { setBenchmarkAvatarId(event.target.value); setFullBodyReferenceUrl(""); setFullBodyReferenceApproved(false); setFullBodyReferenceFit({ status: "idle", missing: [] }); setManualSourceReview({ head: false, hands: false, feet: false, framing: false }); }} style={{ ...selectStyle, display: "block", width: "100%", marginTop: 4 }} disabled={!benchmarkAvatars.length || busy}>
            {!benchmarkAvatars.length && <option value="">{benchmarkAvatarsStatus === "loading" ? "Loading generated Avatar Studio avatars…" : benchmarkAvatarsStatus === "error" ? "Avatar list could not be loaded" : "No generated Avatar Studio avatar is available"}</option>}
            {benchmarkAvatars.map((avatar) => <option key={avatar.id} value={avatar.id}>{avatar.name || "OmniMark avatar"}</option>)}
          </select>
        </label>
        {benchmarkAvatarsStatus === "error" && <div style={{ marginTop: 5, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", fontSize: 12, color: "#92400e" }}><span>{benchmarkAvatarsError}</span><button type="button" onClick={() => loadBenchmarkAvatars()} disabled={busy} style={btnSmall}>Retry avatar list</button></div>}
        <div style={{ marginTop: 5, color: "#166534", fontSize: 12 }}>The selected OmniMark-generated avatar is the only visible identity in the QA output. The private package is used only as a temporary performance reference.</div>
        <div style={{ marginTop: 10, padding: 9, borderRadius: 8, background: "#eff6ff", border: "1px solid #bfdbfe", fontSize: 12, color: "#1e3a8a" }}>
          <b>Full-body source review</b><div style={{ marginTop: 3 }}>Create a generated 16:9 head-to-toe source, inspect it, then explicitly approve it. This blocks portrait side-fill and prevents a correct-size image from being mistaken for a usable walking reference.</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
            <button onClick={() => prepareFullBodyReference()} disabled={busy || !benchmarkAvatarId} style={{ ...btnSmall, background: "#1d4ed8", color: "#fff" }}>{fullBodyReferenceUrl ? "Use current reference" : "Prepare full-body reference"}</button>
            {fullBodyReferenceUrl && <a href={fullBodyReferenceUrl} target="_blank" rel="noreferrer" style={{ ...btnSmall, display: "inline-flex", alignItems: "center", textDecoration: "none" }}>Open reference to review</a>}
            {fullBodyReferenceUrl && <button onClick={() => prepareFullBodyReference({ regenerate: true })} disabled={busy} style={{ ...btnSmall, background: "#b45309", color: "#fff" }}>Regenerate reference</button>}
            {fullBodyReferenceUrl && <button onClick={approveFullBodyReference} disabled={busy || fullBodyReferenceApproved || !sourceReviewReady} style={{ ...btnSmall, minHeight: 44, background: "#166534", color: "#fff", opacity: sourceReviewReady ? 1 : .55 }}>{fullBodyReferenceApproved ? "Reference approved" : "Approve reviewed reference"}</button>}
          </div>
          {fullBodyReferenceUrl && <img src={fullBodyReferenceUrl} alt="Generated full-body source under private review" style={{ display: "block", width: "100%", maxHeight: "min(65svh, 560px)", objectFit: "contain", background: "#111827", borderRadius: 8, marginTop: 10 }} />}
          {fullBodyReferenceFit.status === "loading" && <div style={{ marginTop: 7 }}>Checking head-to-toe framing on this device…</div>}
          {fullBodyReferenceFit.status === "full" && <div style={{ marginTop: 7, color: "#166534" }}>✓ On-device pose check found head, hands, hips, legs, and feet. Open the image and visually review it before approval.</div>}
          {fullBodyReferenceFit.status === "not-full" && <div style={{ marginTop: 7, color: "#b45309" }}>Not approvable: the on-device pose check still needs {fullBodyReferenceFit.missing.join(", ")}. No GPU QA can start.</div>}
          {fullBodyReferenceFit.status === "unavailable" && <div style={{ marginTop: 7, color: "#b45309" }}>The automatic pose check is unavailable on this device. Review the inline image and complete the manual checklist; this records a human review instead of pretending the device checked it.</div>}
          {fullBodyReferenceUrl && fullBodyReferenceFit.status !== "full" && <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 6, marginTop: 9 }}>{[["head", "Head fully visible"], ["hands", "Both hands visible"], ["feet", "Legs, shoes, and floor margin visible"], ["framing", "No crop or distorted anatomy"]].map(([key, label]) => <label key={key} style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, color: "#1e3a8a" }}><input type="checkbox" checked={manualSourceReview[key]} onChange={(event) => setManualSourceReview((current) => ({ ...current, [key]: event.target.checked }))} disabled={busy} />{label}</label>)}</div>}
        </div>
        <label style={{ display: "block", marginTop: 9, fontSize: 12, color: "#166534" }}>Optional private smoke-test script (leave blank for OmniMark’s lip-sync and motion check)<textarea value={qaScript} onChange={(event) => setQaScript(event.target.value)} maxLength={2000} rows={3} style={{ ...selectStyle, width: "100%", marginTop: 4, resize: "vertical" }} placeholder="Optional: describe the speaking and motion test you want to review." /></label>
        {privatePackagesStatus === "loading" ? <div style={{ marginTop: 7, fontSize: 12, color: "#666" }}>Loading private test packages…</div> : privatePackagesStatus === "error" ? <div style={{ marginTop: 7, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: 12, color: "#92400e" }}><span>{privatePackagesError}</span><button type="button" onClick={() => loadPrivatePackages()} disabled={busy} style={btnSmall}>Retry list</button></div> : privatePackages.length === 0 ? <div style={{ marginTop: 7, fontSize: 12, color: "#666" }}>No private packages stored yet.</div> : (
          <div style={{ display: "grid", gap: 7, marginTop: 8 }}>
            {privatePackages.map((item) => <div key={item.id} style={{ padding: 8, borderRadius: 8, background: "#fff", border: "1px solid #d1fae5", fontSize: 12 }}>
              <b>{item.performer_name}</b> · {item.status === "pending_review" ? "Private QA review" : item.status}
              <span style={{ color: "#666" }}> · stored {item.created_at ? new Date(item.created_at).toLocaleString() : "recently"}</span>
              {item.quality_summary?.face_motion_qa_ready && <div style={{ marginTop: 5, color: "#166534" }}>✓ Face reference and motion driver passed metadata preflight. Visual review is still required.</div>}
              {item.quality_summary?.full_body_reference_ready && <div style={{ marginTop: 3, color: "#166534" }}>✓ Full-body reference assets are ready for the separate OmniMotion stage when that worker is available.</div>}
              {item.media_preflight && <div style={{ marginTop: 6, padding: 7, borderRadius: 7, background: "#f8fafc", border: "1px solid #e2e8f0", color: "#334155" }}>
                <b>Private media preflight</b>
                <div style={{ marginTop: 3 }}>Speech reference: {item.media_preflight.selection?.speech_quality_reference?.asset || "not selected"} · Motion reference: {item.media_preflight.selection?.head_motion?.asset || "not selected"} (not transferred) · Full body: {item.media_preflight.selection?.full_body_motion?.asset || "not selected"}</div>
                <div style={{ marginTop: 3, color: "#166534" }}>Suitable for: {item.media_preflight.benchmark_recommendation?.roles?.length ? item.media_preflight.benchmark_recommendation.roles.join(" · ") : "metadata review only"}</div>
                <div style={{ marginTop: 3, color: "#64748b" }}>{item.media_preflight.benchmark_recommendation?.basis || "Visual review is required before this package can validate an OmniMark avatar."}</div>
                {item.media_preflight.assets?.motion_driver && <div style={{ marginTop: 3 }}>Driver: {item.media_preflight.assets.motion_driver.width || "?"}×{item.media_preflight.assets.motion_driver.height || "?"} · {item.media_preflight.assets.motion_driver.duration_seconds || "?"} sec · {item.media_preflight.assets.motion_driver.fps || "?"} FPS · {item.media_preflight.assets.motion_driver.quality_signals?.sample_count || 0} private quality samples</div>}
                {item.media_preflight.assets?.motion_driver?.notes?.length > 0 && <div style={{ marginTop: 3, color: "#92400e" }}>{item.media_preflight.assets.motion_driver.notes.join(" · ")}</div>}
              </div>}
              {item.status === "pending_review" && <label style={{ display: "inline-block", marginLeft: 8, cursor: busy ? "default" : "pointer", color: "#166534", fontWeight: 700 }}>Attach close face image<input type="file" accept="image/jpeg,image/png,image/webp" disabled={busy} onChange={(event) => attachFaceReference(item, event)} style={{ display: "none" }} /></label>}
              {item.status === "pending_review" && <button onClick={() => startQaRender(item)} disabled={busy || qaJob?.status === "processing" || !item.face_reference_attached} title={item.face_reference_attached ? "" : "Attach a close face reference before starting portrait QA."} style={{ ...btnSmall, marginLeft: 8, padding: "4px 8px", background: "#111827", color: "#fff" }}>Run portrait QA</button>}
              {item.status === "pending_review" && <button onClick={() => startQaRender(item, "full_body", "walk_and_talk")} disabled={busy || qaJob?.status === "processing" || !item.quality_summary?.full_body_reference_ready || !fullBodyReferenceApproved} title={!item.quality_summary?.full_body_reference_ready ? "A verified 4–30 second performance video is required before full-body QA." : !fullBodyReferenceApproved ? "Prepare, inspect, and approve the generated full-body avatar reference first." : ""} style={{ ...btnSmall, marginLeft: 8, padding: "4px 8px", background: "#1d4ed8", color: "#fff" }}>Run walk-and-talk full-body QA</button>} {item.status === "pending_review" && <button onClick={() => startQaRender(item, "full_body", "walk_and_talk", "private_capture_experimental")} disabled={busy || qaJob?.status === "processing" || !item.quality_summary?.full_body_reference_ready || !fullBodyReferenceApproved} title={!item.quality_summary?.full_body_reference_ready ? "A verified 4–30 second performance video is required before full-body QA." : !fullBodyReferenceApproved ? "Prepare, inspect, and approve the generated full-body avatar reference first." : "Runs an automated hand-pose preflight before this private capture is sent to the private QA worker."} style={{ ...btnSmall, marginLeft: 8, padding: "4px 8px", background: "#0f766e", color: "#fff" }}>Use this private capture for QA</button>}
              {!item.face_reference_attached && <div style={{ marginTop: 6, fontSize: 12, color: "#92400e" }}>Required for portrait QA: attach a well-lit close face image. Private performance media is kept as a visual reference; direct motion transfer is disabled in this path.</div>}
            </div>)}
          </div>
        )}
        {qaJob && <div style={{ marginTop: 9, padding: 9, borderRadius: 8, background: "#eff6ff", border: "1px solid #bfdbfe", fontSize: 12 }}>
          <b>Private QA render:</b> {qaJob.status || "processing"}{qaJob.status === "processing" ? " — processing in the background; you may leave this page." : ""}
          {qaJob.status === "completed" && qaJob.output?.video_url && <> · <a href={qaJob.output.video_url} target="_blank" rel="noreferrer">Open QA video</a></>}
          {qaJob.status === "completed" && qaJob.output?.technical_quality_gate_passed && qaJob.output?.visual_review_status !== "admin_approved" && <div style={{ marginTop: 8, display: "grid", gap: 7 }}>
            <b>Required visual QA rubric</b><span>Rate each item 1–5. Every score must be 4 or 5 to {Number(qaJob.input?.target_duration_seconds) === 4 ? "unlock a 30-second private review." : "record this final private 30-second review."}</span>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 6 }}>{qaReviewFields.map(([key, label]) => <label key={key}>{label}<select value={qaReview[key]} onChange={(event) => setQaReview((current) => ({ ...current, [key]: Number(event.target.value) }))} disabled={busy} style={{ ...selectStyle, width: "100%" }}><option value={0}>Select score</option>{[1, 2, 3, 4, 5].map((score) => <option key={score} value={score}>{score} / 5</option>)}</select></label>)}</div>
            <label>Review notes (optional)<textarea value={qaReview.notes} onChange={(event) => setQaReview((current) => ({ ...current, notes: event.target.value }))} maxLength={2000} rows={2} disabled={busy} style={{ ...selectStyle, width: "100%", resize: "vertical" }} /></label>
            <button onClick={approveQaVisualReview} disabled={busy || !qaReviewReady} style={{ ...btnSmall, width: "fit-content", padding: "4px 8px", background: qaReviewReady ? "#047857" : "#94a3b8", color: "#fff" }}>Record passing visual review</button>
          </div>}
          {qaJob.output?.visual_review_status === "admin_approved" && Number(qaJob.input?.target_duration_seconds) === 4 && <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}><span style={{ color: "#166534" }}>✓ Visual review approved for a private 30-second QA review.</span><button onClick={startThirtySecondQaReview} disabled={busy} style={{ ...btnSmall, padding: "4px 8px", background: "#047857", color: "#fff" }}>Run 30-second portrait QA review</button></div>}
          {qaJob.output?.visual_review_status === "admin_approved" && Number(qaJob.input?.target_duration_seconds) === 30 && <div style={{ marginTop: 8, color: "#166534" }}>✓ Final private 30-second visual review recorded. Customer publishing remains disabled until the separate launch audit is complete.</div>}
        </div>}
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10, fontSize: 12, color: "#166534" }}>
        {identityImage && <span>✓ Identity frame</span>}{faceReferenceFile && <span>✓ Close face reference</span>}{driverVideo && <span>✓ Motion driver</span>}{releaseFile && <span>✓ Signed release</span>}{cameraState === "recording" && <span>● Recording current guided step</span>}
      </div>
      {message && <div style={{ marginTop: 10, color: "#166534", fontSize: 13 }}>{message}</div>}
      {error && <div style={{ marginTop: 10, color: "#b00020", fontSize: 13 }}>{error}</div>}
    </section>
  );
}

function StatCard({ label, value }) {
  return (
    <div style={{ ...card, textAlign: "center", padding: 16 }}>
      <div style={{ fontSize: 28, fontWeight: 900 }}>{value}</div>
      <div style={{ fontSize: 13, color: "#666", marginTop: 4 }}>{label}</div>
    </div>
  );
}

const card = {
  borderRadius: 14,
  border: "1px solid rgba(0,0,0,0.10)",
  padding: 12,
  background: "#fff",
};

const selectStyle = {
  borderRadius: 8,
  border: "1px solid rgba(0,0,0,0.12)",
  padding: "6px 10px",
  fontSize: 13,
  background: "#fff",
};

const btnSmall = {
  borderRadius: 8,
  border: "1px solid rgba(0,0,0,0.12)",
  padding: "6px 12px",
  fontSize: 13,
  fontWeight: 700,
  background: "#fff",
  cursor: "pointer",
};
